import React, { useState } from 'react';
import { UserPlus, Edit2, Trash2, Key, Save, X, Eye, EyeOff, Shield, Mail, Phone, User as UserIcon, AlertCircle, Check } from 'lucide-react';
import { User, Role } from '../../types';
import { Button } from '../shared';

interface UserManagementProps {
    users: User[];
    onUpdateUser: (user: User & { password?: string }) => void;
    onDeleteUser: (userId: string) => void;
    onCreateUser: (user: Omit<User, 'id'> & { password: string }) => void;
}

const ROLE_CONFIG = {
    STORE: { label: 'Tienda', color: 'bg-gray-100 text-gray-700', description: 'Acceso solo a tickets de su tienda' },
    TECH: { label: 'Técnico', color: 'bg-blue-100 text-blue-700', description: 'Puede gestionar tickets asignados' },
    HELPDESK: { label: 'Mesa Central', color: 'bg-purple-100 text-purple-700', description: 'Acceso completo, asignar técnicos' },
    COORD: { label: 'Coordinador', color: 'bg-orange-100 text-orange-700', description: 'Supervisión y gestión global' },
    SUPERVISOR: { label: 'Supervisor', color: 'bg-green-100 text-green-700', description: 'Administración completa' },
};

export const UserManagement: React.FC<UserManagementProps> = ({
    users,
    onUpdateUser,
    onDeleteUser,
    onCreateUser
}) => {
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editedUser, setEditedUser] = useState<Partial<User> | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterRole, setFilterRole] = useState<Role | 'ALL'>('ALL');

    // Include all roles
    const roles: Role[] = ['STORE', 'TECH', 'HELPDESK', 'COORD', 'SUPERVISOR'];

    const filteredUsers = users.filter(user => {
        // Allow all users
        const matchesSearch = user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (user.email && user.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
            user.role.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesRole = filterRole === 'ALL' || user.role === filterRole;
        return matchesSearch && matchesRole;
    });

    const handleEdit = (user: User) => {
        setIsEditing(true);
        setEditedUser({ ...user });
        setShowModal(true);
    };

    const handleDelete = (userId: string) => {
        const user = users.find(u => u.id === userId);
        if (window.confirm(`¿Eliminar usuario "${user?.name}"? Esta acción no se puede deshacer.`)) {
            onDeleteUser(userId);
        }
    };

    const handleCreateNew = () => {
        setIsEditing(false);
        setEditedUser({
            name: '',
            role: 'TECH',
            avatar: '',
            password: '',
            email: '',
            phone: '',
        });
        setShowModal(true);
    };

    const handleSave = () => {
        if (!editedUser) return;

        // Validations
        if (!editedUser.name || editedUser.name.trim().length < 3) {
            alert('El nombre debe tener al menos 3 caracteres');
            return;
        }

        // Password validation:
        // - REQUIRED for NEW users
        // - OPTIONAL for editing (only update if provided)
        if (!isEditing) {
            // Creating new user - password is required
            if (!editedUser.password || editedUser.password.length < 3) {
                alert('La contraseña debe tener al menos 3 caracteres');
                return;
            }
        } else {
            // Editing existing user - password is optional
            // Only validate if user entered something
            if (editedUser.password && editedUser.password.length > 0 && editedUser.password.length < 3) {
                alert('Si desea cambiar la contraseña, debe tener al menos 3 caracteres');
                return;
            }
        }

        if (!editedUser.role) {
            alert('Debe seleccionar un rol');
            return;
        }

        // Generate avatar if empty
        if (!editedUser.avatar) {
            editedUser.avatar = editedUser.name.substring(0, 2).toUpperCase();
        }

        if (isEditing && editedUser.id) {
            console.log('🔥🔥🔥 [UserManagement] CALLING onUpdateUser', {
                userId: editedUser.id,
                hasPassword: !!editedUser.password,
                passwordLength: editedUser.password?.length
            });

            // Create update object, only include password if it was changed
            const updates = { ...editedUser } as User & { password?: string };
            if (!editedUser.password || editedUser.password.trim().length === 0) {
                // Password field is empty = user doesn't want to change it
                delete updates.password;
            }

            onUpdateUser(updates);
        } else {
            // For new users, password is required
            onCreateUser(editedUser as (Omit<User, 'id'> & { password: string }));
        }

        handleCancel();
    };

    const handleCancel = () => {
        setShowModal(false);
        setEditedUser(null);
        setShowPassword(false);
        setIsEditing(false);
    };

    const generateRandomPassword = () => {
        if (!editedUser) return;
        const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
        const password = Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
        setEditedUser({ ...editedUser, password });
        setShowPassword(true);
    };

    const generateAvatar = () => {
        if (!editedUser || !editedUser.name) return;
        const avatar = editedUser.name.substring(0, 2).toUpperCase();
        setEditedUser({ ...editedUser, avatar });
    };

    const getRoleStats = () => {
        return roles.map(role => ({
            role,
            count: users.filter(u => u.role === role).length,
            ...ROLE_CONFIG[role]
        }));
    };

    return (
        <div className="bg-white p-6 rounded-xl border shadow-sm">
            <div className="mb-6 flex justify-between items-start">
                <div>
                    <h2 className="text-xl font-bold mb-1">Gestión de Usuarios</h2>
                    <p className="text-gray-500 text-sm">Administra usuarios, roles y permisos del sistema</p>
                </div>
                <Button onClick={handleCreateNew} variant="primary">
                    <UserPlus size={18} className="mr-2" /> Crear Usuario
                </Button>
            </div>

            {/* Filters */}
            <div className="mb-6 flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                    <input
                        type="text"
                        placeholder="Buscar por nombre, email o rol..."
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
                <div>
                    <select
                        className="px-4 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-black"
                        value={filterRole}
                        onChange={(e) => setFilterRole(e.target.value as Role | 'ALL')}
                    >
                        <option value="ALL">Todos los Roles</option>
                        {roles.map(role => (
                            <option key={role} value={role}>{ROLE_CONFIG[role].label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Stats by Role */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                {getRoleStats().map(stat => (
                    <div key={stat.role} className={`${stat.color} p-3 rounded-lg border-2`}>
                        <p className="text-xs font-bold uppercase opacity-70 mb-1">{stat.label}</p>
                        <p className="text-2xl font-bold">{stat.count}</p>
                    </div>
                ))}
            </div>

            {/* Users Table */}
            <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                    <thead className="bg-gray-50 text-gray-500 uppercase font-bold text-xs">
                        <tr>
                            <th className="px-4 py-3">Usuario</th>
                            <th className="px-4 py-3">Rol</th>
                            <th className="px-4 py-3">Email</th>
                            <th className="px-4 py-3">Teléfono</th>
                            <th className="px-4 py-3">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {filteredUsers.map(user => (
                            <tr key={user.id} className="hover:bg-gray-50 transition">
                                <td className="px-4 py-3">
                                    <div className="flex items-center">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm mr-3 ${ROLE_CONFIG[user.role].color}`}>
                                            {user.avatar}
                                        </div>
                                        <div>
                                            <div className="font-medium text-gray-900">{user.name}</div>
                                            <div className="text-xs text-gray-500">ID: {user.id}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-4 py-3">
                                    <span className={`px-2 py-1 rounded text-xs font-bold ${ROLE_CONFIG[user.role].color} border`}>
                                        {ROLE_CONFIG[user.role].label}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-gray-600 text-xs">{user.email || '--'}</td>
                                <td className="px-4 py-3 text-gray-600 text-xs font-mono">{user.phone || '--'}</td>
                                <td className="px-4 py-3">
                                    <div className="flex space-x-2">
                                        <button onClick={() => handleEdit(user)} className="text-blue-600 hover:text-blue-800 p-1" title="Editar">
                                            <Edit2 size={16} />
                                        </button>
                                        <button onClick={() => handleDelete(user.id)} className="text-red-600 hover:text-red-800 p-1" title="Eliminar">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {filteredUsers.length === 0 && (
                    <div className="py-12 text-center text-gray-400">
                        <UserIcon size={48} className="mx-auto mb-3 opacity-50" />
                        <p>No se encontraron usuarios</p>
                    </div>
                )}
            </div>

            {/* Create/Edit Modal */}
            {showModal && editedUser && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-in fade-in">
                    <div className="bg-white rounded-xl w-full max-w-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 max-h-[90vh] flex flex-col">
                        <div className="bg-gray-100 px-6 py-4 border-b flex justify-between items-center">
                            <div>
                                <h3 className="font-bold text-gray-900">
                                    {isEditing ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
                                </h3>
                                <p className="text-xs text-gray-500">
                                    {isEditing ? `ID: ${editedUser.id}` : 'Complete los datos del nuevo usuario'}
                                </p>
                            </div>
                            <button onClick={handleCancel} className="text-gray-500 hover:text-black transition">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-6 space-y-6 overflow-y-auto flex-1">
                            {/* User Info */}
                            <div>
                                <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                                    <UserIcon size={18} className="mr-2" />
                                    Información del Usuario
                                </h4>

                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Nombre Completo *</label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black"
                                            placeholder="Ej: Juan Pérez González"
                                            value={editedUser.name || ''}
                                            onChange={(e) => setEditedUser({ ...editedUser, name: e.target.value })}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Avatar (2 letras) *</label>
                                        <div className="flex space-x-2">
                                            <input
                                                type="text"
                                                maxLength={2}
                                                className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black uppercase font-bold text-center"
                                                placeholder="JP"
                                                value={editedUser.avatar || ''}
                                                onChange={(e) => setEditedUser({ ...editedUser, avatar: e.target.value.toUpperCase() })}
                                            />
                                            <button
                                                onClick={generateAvatar}
                                                className="px-3 py-2 bg-gray-100 border rounded-lg hover:bg-gray-200 text-xs font-medium"
                                            >
                                                Auto
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                            <Shield size={14} className="mr-1" /> Rol / Perfil *
                                        </label>
                                        <select
                                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black"
                                            value={editedUser.role || 'TECH'}
                                            onChange={(e) => setEditedUser({ ...editedUser, role: e.target.value as Role })}
                                        >
                                            {roles.map(role => (
                                                <option key={role} value={role}>{ROLE_CONFIG[role].label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {editedUser.role && (
                                        <div className="md:col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                                            <p className="text-xs text-blue-800">
                                                <strong>Permisos de {ROLE_CONFIG[editedUser.role].label}:</strong> {ROLE_CONFIG[editedUser.role].description}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Password Section */}
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <div className="flex items-start mb-3">
                                    <Key className="text-yellow-700 mr-2 mt-0.5" size={20} />
                                    <div className="flex-1">
                                        <h4 className="font-bold text-yellow-900 mb-1">Contraseña de Acceso *</h4>
                                        <p className="text-xs text-yellow-700">Esta contraseña será usada para ingresar al sistema</p>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <div className="relative">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            className="w-full px-4 py-2 border rounded-lg pr-20 focus:ring-2 focus:ring-yellow-500 font-mono"
                                            placeholder="Contraseña..."
                                            value={editedUser.password || ''}
                                            onChange={(e) => setEditedUser({ ...editedUser, password: e.target.value })}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                                        >
                                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={generateRandomPassword}
                                        className="text-sm text-yellow-700 hover:text-yellow-900 font-medium flex items-center"
                                    >
                                        <Key size={14} className="mr-1" /> Generar Contraseña Aleatoria
                                    </button>

                                    {editedUser.password && editedUser.password.length < 3 && (
                                        <div className="flex items-center text-red-600 text-xs">
                                            <AlertCircle size={12} className="mr-1" />
                                            Mínimo 3 caracteres
                                        </div>
                                    )}

                                    {editedUser.password && editedUser.password.length >= 3 && (
                                        <div className="flex items-center text-green-600 text-xs">
                                            <Check size={12} className="mr-1" />
                                            Contraseña válida
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Contact Info */}
                            <div>
                                <h4 className="font-bold text-gray-900 mb-3">Información de Contacto (Opcional)</h4>

                                <div className="grid md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                            <Mail size={14} className="mr-1" /> Email
                                        </label>
                                        <input
                                            type="email"
                                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black"
                                            placeholder="usuario@ejemplo.com"
                                            value={editedUser.email || ''}
                                            onChange={(e) => setEditedUser({ ...editedUser, email: e.target.value })}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                            <Phone size={14} className="mr-1" /> Teléfono
                                        </label>
                                        <input
                                            type="tel"
                                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black font-mono"
                                            placeholder="+56 9 1234 5678"
                                            value={editedUser.phone || ''}
                                            onChange={(e) => setEditedUser({ ...editedUser, phone: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gray-50 px-6 py-4 border-t flex justify-between">
                            <Button onClick={handleCancel} variant="secondary">
                                Cancelar
                            </Button>
                            <Button onClick={handleSave} variant="primary">
                                <Save size={16} className="mr-2" /> {isEditing ? 'Guardar Cambios' : 'Crear Usuario'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
